import { ResponseMessage } from '../types';
import { sanitizeHTML, sanitizeURL } from '../../shared/utils/sanitize';

/**
 * Creates a right-oriented user message bubble and appends it to the message container.
 * @param messageContainer The container element for chat messages.
 * @param input The input textarea for user messages.
 * @param data The message content to send.
 */
function sendAndAppendMessage(
  messageContainer: HTMLDivElement,
  input: HTMLTextAreaElement,
  data: string
) {
  const wrapper = document.createElement('div');
  wrapper.className = 'chat-message user';

  const avatarDiv = document.createElement('div');
  // avatarDiv.className = 'chat-message-icon';
  avatarDiv.innerHTML = `
   <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 27 27" fill="none">
  <g clip-path="url(#clip0_983_497)">
    <path d="M0 13.5C0 6.04415 6.04415 0 13.5 0C20.9558 0 27 6.04415 27 13.5C27 20.9558 20.9558 27 13.5 27C6.04415 27 0 20.9558 0 13.5Z" fill="#FFDAC2"/>
    <path d="M13.4827 18.6201C16.5368 18.6201 19.0127 16.1442 19.0127 13.09C19.0127 10.0359 16.5368 7.55994 13.4827 7.55994C10.4285 7.55994 7.95258 10.0359 7.95258 13.09C7.95258 16.1442 10.4285 18.6201 13.4827 18.6201Z" fill="#FF7F81"/>
    <path d="M13.5005 26.9999C16.7369 26.9999 19.7073 25.8612 22.0327 23.9624C21.8779 23.7779 21.6978 23.5969 21.491 23.4208C19.3674 21.612 16.4872 20.5959 13.484 20.5959C10.4808 20.5959 7.60066 21.612 5.47709 23.4208C5.27623 23.5919 5.10057 23.7675 4.94879 23.9464C7.27718 25.8548 10.2551 26.9999 13.5005 26.9999Z" fill="#DB3672"/>
  </g>
  <defs>
    <clipPath id="clip0_983_497">
      <rect width="27" height="27" fill="white"/>
    </clipPath>
  </defs>
</svg>
  `;

  const respondDiv = document.createElement('div');
  respondDiv.className = 'chat-bubble-user';

  const p = document.createElement('p');
  p.textContent = data;
  respondDiv.appendChild(p);

  wrapper.appendChild(respondDiv);
  wrapper.appendChild(avatarDiv);

  messageContainer.appendChild(wrapper);
  input.value = '';
  messageContainer.scrollTop = messageContainer.scrollHeight;
}

/**
 * Receives a response and creates a left-oriented bot message bubble in the message container.
 * @param messageContainer The container element for chat messages.
 * @param data A promise resolving to the response content.
 */
function getPrimaryMessageWrapper(
  messageContainer: HTMLDivElement,
  backgroundColor?: string
): HTMLDivElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'chat-message';

  const avatarDiv = document.createElement('div');
  avatarDiv.className = 'chat-message-icon';
  if (backgroundColor) avatarDiv.style.backgroundColor = backgroundColor;

  avatarDiv.innerHTML = `
   <svg class="svg-class-chat" xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
  <path d="M7.49512 13.794C7.49516 14.0659 7.81482 14.212 8.02051 14.0342L11.5801 10.9541L12.9727 9.74906L12.9873 9.73734C13.0683 9.67142 13.1474 9.60265 13.2246 9.53226L13.2432 9.51566C13.6429 9.14412 13.9869 8.71437 14.2617 8.23832C14.7308 7.42771 14.9999 6.4845 15 5.47758C15 5.15809 14.9729 4.84319 14.9209 4.5391C14.8091 3.8841 14.5822 3.26891 14.2617 2.71586C13.3218 1.09196 11.5777 7.71891e-05 9.58203 3.8263e-05L5.41894 3.86269e-05C3.42261 3.88014e-05 1.67818 1.09194 0.73828 2.71586C0.269238 3.52641 0.000111226 4.47073 -7.54858e-07 5.47758C6.32593e-05 6.4845 0.26922 7.42771 0.738281 8.23832C1.67814 9.86306 3.42248 10.9541 5.41895 10.9541L7.17773 10.9541C7.35288 10.9542 7.49478 11.0974 7.49512 11.2725L7.49512 13.794ZM7.85645 10.5948C7.71261 10.7137 7.49512 10.6106 7.49512 10.4239L7.49512 8.5557C7.49468 8.38072 7.35281 8.23836 7.17773 8.23832L5.33105 8.23832C5.2612 8.23768 5.18548 8.23497 5.10547 8.22855C5.02705 8.22215 4.95345 8.21305 4.88574 8.20219L4.88574 8.20121L4.88477 8.20023C3.58851 7.98553 2.59978 6.84905 2.59961 5.47758C2.59991 4.106 3.58862 2.96608 4.88574 2.75102C5.0305 2.72663 5.17932 2.71586 5.33105 2.71586L9.59863 2.71586L9.59863 2.71488L9.59961 2.71488C11.108 2.71518 12.3307 3.95175 12.3311 5.4766C12.331 5.88499 12.2125 6.28427 12.2061 6.3057C12.0341 6.85278 11.7283 7.21632 11.5498 7.39945L11.3145 7.60355L11.3096 7.60746L10.6611 8.16801L7.85645 10.5948Z" fill="white"/>
  <rect x="8.31921" y="4.54626" width="0.397148" height="2.18431" rx="0.198574" fill="white"/>
  <rect x="5.34085" y="4.74485" width="0.397148" height="1.78716" rx="0.198574" fill="white"/>
  <rect x="7.32642" y="4.34768" width="0.397148" height="2.58146" rx="0.198574" fill="white"/>
  <rect x="10.3048" y="5.14195" width="0.397148" height="1.19144" rx="0.198574" fill="white"/>
  <rect x="4.3475" y="5.14195" width="0.397148" height="1.19144" rx="0.198574" fill="white"/>
  <rect x="9.31201" y="4.74485" width="0.397148" height="1.78716" rx="0.198574" fill="white"/>
  <rect x="6.33365" y="4.54626" width="0.397148" height="2.18431" rx="0.198574" fill="white"/>
</svg>
  `;

  const respondDiv = document.createElement('div');
  respondDiv.className = 'typing-indicator chat-padding';

  wrapper.appendChild(avatarDiv);
  wrapper.appendChild(respondDiv);

  messageContainer.appendChild(wrapper);
  messageContainer.scrollTop = messageContainer.scrollHeight;

  return wrapper;
}

/**
 * Receives a response and creates a left-oriented bot message bubble in the message container.
 * @param messageContainer The container element for chat messages.
 * @param data A promise resolving to the response content.
 */
function showDots(messageContainer: HTMLDivElement, backgroundColor?: string): HTMLDivElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'chat-message';

  const avatarDiv = document.createElement('div');
  avatarDiv.className = 'chat-message-icon';
  if (backgroundColor) avatarDiv.style.backgroundColor = backgroundColor;
  avatarDiv.innerHTML = `
   <svg class="svg-class-chat" xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
  <path d="M7.49512 13.794C7.49516 14.0659 7.81482 14.212 8.02051 14.0342L11.5801 10.9541L12.9727 9.74906L12.9873 9.73734C13.0683 9.67142 13.1474 9.60265 13.2246 9.53226L13.2432 9.51566C13.6429 9.14412 13.9869 8.71437 14.2617 8.23832C14.7308 7.42771 14.9999 6.4845 15 5.47758C15 5.15809 14.9729 4.84319 14.9209 4.5391C14.8091 3.8841 14.5822 3.26891 14.2617 2.71586C13.3218 1.09196 11.5777 7.71891e-05 9.58203 3.8263e-05L5.41894 3.86269e-05C3.42261 3.88014e-05 1.67818 1.09194 0.73828 2.71586C0.269238 3.52641 0.000111226 4.47073 -7.54858e-07 5.47758C6.32593e-05 6.4845 0.26922 7.42771 0.738281 8.23832C1.67814 9.86306 3.42248 10.9541 5.41895 10.9541L7.17773 10.9541C7.35288 10.9542 7.49478 11.0974 7.49512 11.2725L7.49512 13.794ZM7.85645 10.5948C7.71261 10.7137 7.49512 10.6106 7.49512 10.4239L7.49512 8.5557C7.49468 8.38072 7.35281 8.23836 7.17773 8.23832L5.33105 8.23832C5.2612 8.23768 5.18548 8.23497 5.10547 8.22855C5.02705 8.22215 4.95345 8.21305 4.88574 8.20219L4.88574 8.20121L4.88477 8.20023C3.58851 7.98553 2.59978 6.84905 2.59961 5.47758C2.59991 4.106 3.58862 2.96608 4.88574 2.75102C5.0305 2.72663 5.17932 2.71586 5.33105 2.71586L9.59863 2.71586L9.59863 2.71488L9.59961 2.71488C11.108 2.71518 12.3307 3.95175 12.3311 5.4766C12.331 5.88499 12.2125 6.28427 12.2061 6.3057C12.0341 6.85278 11.7283 7.21632 11.5498 7.39945L11.3145 7.60355L11.3096 7.60746L10.6611 8.16801L7.85645 10.5948Z" fill="white"/>
  <rect x="8.31921" y="4.54626" width="0.397148" height="2.18431" rx="0.198574" fill="white"/>
  <rect x="5.34085" y="4.74485" width="0.397148" height="1.78716" rx="0.198574" fill="white"/>
  <rect x="7.32642" y="4.34768" width="0.397148" height="2.58146" rx="0.198574" fill="white"/>
  <rect x="10.3048" y="5.14195" width="0.397148" height="1.19144" rx="0.198574" fill="white"/>
  <rect x="4.3475" y="5.14195" width="0.397148" height="1.19144" rx="0.198574" fill="white"/>
  <rect x="9.31201" y="4.74485" width="0.397148" height="1.78716" rx="0.198574" fill="white"/>
  <rect x="6.33365" y="4.54626" width="0.397148" height="2.18431" rx="0.198574" fill="white"/>
</svg>
  `;

  const respondDiv = document.createElement('div');
  respondDiv.className = 'typing-indicator chat-padding';
  respondDiv.innerHTML = `
    <div class="typing-indicator">
      <div class="typing-dots"></div>
      <div class="typing-dots"></div>
      <div class="typing-dots"></div>
    </div>
  `;

  wrapper.appendChild(avatarDiv);
  wrapper.appendChild(respondDiv);

  messageContainer.appendChild(wrapper);
  messageContainer.scrollTop = messageContainer.scrollHeight;

  return wrapper;
}

/**
 * Converts simple Markdown-like syntax to HTML with URL sanitization.
 * Currently supports: **bold**, line breaks, and numbered lists.
 */
function parseMarkdownToHTML(text: string): string {
  return (
    text
      // Convert Markdown links [text](url) with URL sanitization
      .replace(
        /\[([^\]]+)\]\(([^)]+)\)/g,
        (_match, linkText, url) => {
          const safeUrl = sanitizeURL(url);
          return `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer">${linkText}</a>`;
        }
      )
      // Convert **bold**
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      // Convert numbered lists
      .replace(/(\d+)\.\s/g, '<br><strong>$1.</strong> ')
      // Convert dashes with ✅
      .replace(/-\s✅/g, '<br>✅')
      // Convert regular line breaks
      .replace(/\n/g, '<br>')
  );
}

/**
 * Receives a response and creates a left-oriented bot message bubble in the message container.
 * @param wrapper The outer message bubble wrapper.
 * @param messageContainer The container element for chat messages.
 * @param data A promise resolving to the response content.
 */
async function recieveAndAppendMessage(
  wrapper: HTMLDivElement,
  messageContainer: HTMLDivElement,
  data: Promise<ResponseMessage>
): Promise<void> {
  const respondDiv = wrapper.getElementsByClassName('typing-indicator')[0] as HTMLDivElement;
  const response = await data;
  respondDiv.classList.add('chat-bubble-ai');
  const formattedContent = parseMarkdownToHTML(response.content).replace(
    /(<br\s*\/?>\s*){2,}/gi,
    '<br><br>'
  );

  // Sanitize HTML to prevent XSS attacks
  const sanitizedContent = sanitizeHTML(formattedContent);

  respondDiv.innerHTML = `
    <p class="">${sanitizedContent}</p>
  `;

  // Ensure the whole message (starting from top) scrolls into view
  wrapper.scrollIntoView({
    behavior: 'smooth',
    block: 'start', // aligns the top of wrapper with top of container
  });
}

/**
 * Returns the current time in HH:MM AM/PM format.
 */
function getCurrentTime(): string {
  const now = new Date();
  return now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export { getPrimaryMessageWrapper, recieveAndAppendMessage, sendAndAppendMessage, showDots };
